import type { DrawingStyle } from "lightweight-charts-drawing"
import { useState } from "react"
import { ColorSwatchButton } from "../../ui/ColorPicker.tsx"
import { BackIcon, CloneIcon, EyeOffIcon, FrontIcon, GearIcon, LockIcon, TrashIcon, UnlockIcon } from "../../ui/icons.tsx"
import { fillColorOf, fillPatch, settingsModel, textColorOf } from "./drawingSettings.ts"
import type { PaneDrawings, SelectedDrawing } from "./PaneDrawings.ts"

// TradingView's floating toolbar for the selected drawing: colour (with opacity
// and custom colours), text colour, width, line style, settings, lock, hide,
// clone, order, delete.

const WIDTHS = [1, 2, 3, 4]
const LINE_STYLES: ReadonlyArray<DrawingStyle["lineStyle"]> = ["solid", "dashed", "dotted"]

type Popover = "width" | "style" | null

const LineSample = ({ lineStyle = "solid", width }: { readonly width: number; readonly lineStyle?: DrawingStyle["lineStyle"] }) => (
  <svg width="28" height="10" aria-hidden>
    <line
      x1="2"
      y1="5"
      x2="26"
      y2="5"
      stroke="currentColor"
      strokeWidth={width}
      strokeDasharray={lineStyle === "dashed" ? "6 3" : lineStyle === "dotted" ? "1.5 2.5" : undefined}
      strokeLinecap="round"
    />
  </svg>
)

export function DrawingFloatingToolbar({ drawing, drawings, onOpenSettings }: {
  readonly drawing: NonNullable<SelectedDrawing>
  readonly drawings: PaneDrawings
  readonly onOpenSettings: () => void
}) {
  const model = settingsModel(drawing)
  const [open, setOpen] = useState<Popover>(null)
  const toggle = (popover: Popover) => setOpen(open === popover ? null : popover)
  const style = drawing.style
  const pick = (patch: Partial<DrawingStyle>) => {
    drawings.setStyle(patch)
    setOpen(null)
  }

  return (
    <div className="floating-toolbar" onPointerDown={(event) => event.stopPropagation()}>
      <span className="floating-title">{drawing.name ?? drawing.kind.replace(/-/g, " ")}</span>

      {model.hasLine && (
        <span className="floating-item">
          <ColorSwatchButton
            title={model.isShape ? "Border colour" : "Line colour"}
            color={style.color}
            onColor={(color) => drawings.setStyle({ color })}
          />
        </span>
      )}

      {model.hasBackground && (
        <span className="floating-item">
          <ColorSwatchButton title="Background colour" color={fillColorOf(drawing)} onColor={(color) => drawings.setStyle(fillPatch(color))} />
        </span>
      )}

      {model.hasText && (
        <span className="floating-item">
          <ColorSwatchButton
            title="Text colour"
            color={textColorOf(drawing)}
            onColor={(color) => drawings.setStyle({ [model.textColorKey]: color })}
          />
        </span>
      )}

      {model.hasLine && (
        <>
          <span className="floating-item">
            <button type="button" title="Line width" onClick={() => toggle("width")}>
              <LineSample width={style.width} />
              <span className="floating-label">{style.width}px</span>
            </button>
            {open === "width" && (
              <div className="floating-popover">
                {WIDTHS.map((width) => (
                  <button key={width} type="button" aria-pressed={style.width === width} onClick={() => pick({ width })}>
                    <LineSample width={width} /> {width}px
                  </button>
                ))}
              </div>
            )}
          </span>

          <span className="floating-item">
            <button type="button" title="Line style" onClick={() => toggle("style")}>
              <LineSample width={2} lineStyle={style.lineStyle} />
            </button>
            {open === "style" && (
              <div className="floating-popover">
                {LINE_STYLES.map((lineStyle) => (
                  <button key={lineStyle} type="button" aria-pressed={style.lineStyle === lineStyle} onClick={() => pick({ lineStyle })}>
                    <LineSample width={2} lineStyle={lineStyle} /> {lineStyle}
                  </button>
                ))}
              </div>
            )}
          </span>
        </>
      )}

      <span className="floating-divider" />
      <button type="button" title="Settings" onClick={onOpenSettings}>
        <GearIcon />
      </button>
      <button type="button" title={drawing.locked ? "Unlock" : "Lock"} aria-pressed={drawing.locked === true} onClick={() => drawings.setLocked(!drawing.locked)}>
        {drawing.locked ? <LockIcon /> : <UnlockIcon />}
      </button>
      <button type="button" title="Hide" onClick={() => drawings.hideSelected()}>
        <EyeOffIcon />
      </button>
      <button type="button" title="Clone" onClick={() => drawings.cloneSelected()}>
        <CloneIcon />
      </button>
      <button type="button" title="Bring to front" onClick={() => drawings.bringToFront()}>
        <FrontIcon />
      </button>
      <button type="button" title="Send to back" onClick={() => drawings.sendToBack()}>
        <BackIcon />
      </button>
      <button type="button" title={drawing.locked ? "Unlock to remove" : "Remove"} disabled={drawing.locked === true} onClick={() => drawings.removeSelected()}>
        <TrashIcon />
      </button>
    </div>
  )
}
